import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/journeys", label: "Journeys" },
  { to: "/experiences", label: "Experiences" },
  { to: "/signature-journey", label: "2026 Signature" },
  { to: "/lens-of-jeet-aman", label: "The Lens" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Enquire" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-md bg-[color:var(--ivory)]/85 border-b border-[color:var(--line)]"
            : "bg-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between py-5 md:py-6">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <span
              className="inline-block h-[8px] w-[8px] rotate-45"
              style={{ background: "var(--gold)" }}
            />
            <span className="font-display text-2xl md:text-3xl tracking-tight leading-none">
              Njia<span className="italic font-italic-display text-[color:var(--forest)]"> Safaris</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `link-under whitespace-nowrap ${isActive ? "opacity-100" : "opacity-80 hover:opacity-100"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className="md:hidden inline-flex items-center gap-2"
          >
            <span className="eyebrow">Menu</span>
            <Menu size={18} strokeWidth={1.4} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] section-dark flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="container-x flex items-center justify-between py-5">
              <span className="font-display text-2xl text-[color:var(--ivory)]">
                Njia <span className="italic text-[color:var(--gold-3)]">Safaris</span>
              </span>
              <button
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                className="text-[color:var(--ivory)] inline-flex items-center gap-2"
              >
                <span className="eyebrow eyebrow-invert">Close</span>
                <X size={18} strokeWidth={1.4} />
              </button>
            </div>
            <div className="container-x flex-1 flex flex-col justify-center gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.06, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                >
                  <NavLink
                    to={l.to}
                    className="font-display text-5xl md:text-7xl text-[color:var(--ivory)] hover:text-[color:var(--gold-3)] transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
